import { apiService } from './api';
import type {
  DiagnosticNote,
  TreatmentCptAnalysis,
  CodeSuggestion,
  ProcedureCptSuggestion,
  ProcedureExtractionMetadata,
  EMCode,
  EMGenerateResponseData,
  EmCatalogEntry,
  ProcedureCptAuditResponse,
  SubmitOverrideArgs
} from '../types/codingAssistant';

/**
 * Helper to build base URL for clinical endpoints
 * Supports both appointment mode and encounter mode
 */
function getBaseUrl(appointmentId?: string, encounterId?: string, isEncounterMode?: boolean): string {
  if (isEncounterMode && encounterId) {
    return `/v1/clinical/encounter/${encounterId}`;
  }
  if (appointmentId) {
    return `/v1/clinical/${appointmentId}`;
  }
  throw new Error('Either appointmentId or encounterId must be provided');
}

// Diagnostic Notes API
export async function createDiagnosticNote(
  appointmentId?: string,
  encounterId?: string,
  isEncounterMode?: boolean,
  content?: string,
  plainText?: string,
  dictationAudioUrl?: string
): Promise<{ success: boolean; data: { diagnosticNote: DiagnosticNote } }> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  return await apiService.post<{ success: boolean; data: { diagnosticNote: DiagnosticNote } }>(
    `${baseUrl}/coding-assistant/diagnostic-notes`,
    { content, plainText, dictationAudioUrl }
  );
}

export async function getDiagnosticNotes(
  appointmentId?: string,
  encounterId?: string,
  isEncounterMode?: boolean
): Promise<{ success: boolean; data: { diagnosticNotes: DiagnosticNote[] } }> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  return await apiService.get<{ success: boolean; data: { diagnosticNotes: DiagnosticNote[] } }>(
    `${baseUrl}/coding-assistant/diagnostic-notes`
  );
}

export async function updateDiagnosticNote(
  appointmentId?: string,
  encounterId?: string,
  isEncounterMode?: boolean,
  noteId?: string,
  content?: string,
  plainText?: string
): Promise<{ success: boolean; data: { diagnosticNote: DiagnosticNote } }> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  return await apiService.put<{ success: boolean; data: { diagnosticNote: DiagnosticNote } }>(
    `${baseUrl}/coding-assistant/diagnostic-notes/${noteId}`,
    { content, plainText }
  );
}

export async function searchIcd10Codes(
  appointmentId?: string,
  encounterId?: string,
  isEncounterMode?: boolean,
  noteId?: string,
  searchQuery?: string
): Promise<{ success: boolean; data: { codes: CodeSuggestion[]; searchQuery: string; totalResults: number } }> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  return await apiService.post<{ success: boolean; data: { codes: CodeSuggestion[]; searchQuery: string; totalResults: number } }>(
    `${baseUrl}/coding-assistant/diagnostic-notes/${noteId}/search-codes`,
    { searchQuery }
  );
}

export async function reviewDiagnosticCode(
  appointmentId?: string,
  encounterId?: string,
  isEncounterMode?: boolean,
  noteId?: string,
  codeData?: CodeSuggestion,
  status?: 'approved' | 'rejected',
  diagnosticText?: string,
  rejectionReason?: string
): Promise<{ success: boolean; message: string }> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  return await apiService.post<{ success: boolean; message: string }>(
    `${baseUrl}/coding-assistant/diagnostic-notes/${noteId}/codes/review`,
    { codeData, status, diagnosticText, rejectionReason }
  );
}

// Treatment CPT API
export async function getTreatmentCptAnalyses(
  appointmentId?: string,
  encounterId?: string,
  isEncounterMode?: boolean
): Promise<{ success: boolean; data: { analyses: TreatmentCptAnalysis[] } }> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  return await apiService.get<{ success: boolean; data: { analyses: TreatmentCptAnalysis[] } }>(
    `${baseUrl}/coding-assistant/treatment-cpt`
  );
}

export async function searchCptCodes(
  appointmentId?: string,
  encounterId?: string,
  isEncounterMode?: boolean,
  searchQuery?: string
): Promise<{ success: boolean; data: { codes: CodeSuggestion[]; searchQuery: string; totalResults: number } }> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  return await apiService.post<{ success: boolean; data: { codes: CodeSuggestion[]; searchQuery: string; totalResults: number } }>(
    `${baseUrl}/coding-assistant/treatment-cpt/search-codes`,
    { searchQuery }
  );
}

export async function reviewTreatmentCptCode(
  appointmentId?: string,
  encounterId?: string,
  isEncounterMode?: boolean,
  codeData?: CodeSuggestion,
  status?: 'approved' | 'rejected',
  treatmentText?: string,
  sourceSection?: string,
  rejectionReason?: string
): Promise<{ success: boolean; message: string }> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  return await apiService.post<{ success: boolean; message: string }>(
    `${baseUrl}/coding-assistant/treatment-cpt/codes/review`,
    { codeData, status, treatmentText, sourceSection, rejectionReason }
  );
}

// Procedure CPT API (Layer 2: AI-powered extraction of procedures during visit)
export async function analyzeProcedureCpt(
  appointmentId?: string,
  encounterId?: string,
  isEncounterMode?: boolean
): Promise<{
  success: boolean;
  data: {
    suggestions: ProcedureCptSuggestion[];
    count: number;
    metadata: ProcedureExtractionMetadata;
  };
}> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  return await apiService.post<{
    success: boolean;
    data: {
      suggestions: ProcedureCptSuggestion[];
      count: number;
      metadata: ProcedureExtractionMetadata;
    };
  }>(`${baseUrl}/procedure-cpt/analyze`, {});
}

export async function getProcedureCptSuggestions(
  appointmentId?: string,
  encounterId?: string,
  isEncounterMode?: boolean
): Promise<{ success: boolean; data: { suggestions: ProcedureCptSuggestion[] } }> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  return await apiService.get<{ success: boolean; data: { suggestions: ProcedureCptSuggestion[] } }>(
    `${baseUrl}/procedure-cpt`
  );
}

export async function reviewProcedureCpt(
  appointmentId?: string,
  encounterId?: string,
  isEncounterMode?: boolean,
  cptCode?: string,
  action?: 'approve' | 'reject',
  notes?: string
): Promise<{ success: boolean; message: string; data: any }> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  return await apiService.post<{ success: boolean; message: string; data: any }>(
    `${baseUrl}/procedure-cpt/${cptCode}/review`,
    { action, notes }
  );
}

// =============================================================================
// V3 audit pipeline (F1.5) — frontend reads + override capture
// =============================================================================

/**
 * Fetch the latest V3 audit for an appointment. Returns null if no V3
 * extraction has been run yet (backend returns 404).
 */
export async function getProcedureCptAudit(
  appointmentId?: string,
  encounterId?: string,
  isEncounterMode?: boolean,
): Promise<ProcedureCptAuditResponse | null> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  try {
    const response = await apiService.get<{ success: boolean; data: ProcedureCptAuditResponse }>(
      `${baseUrl}/procedure-cpt/audit`,
    );
    return response.data ?? null;
  } catch (error) {
    // 404 — no audit yet; surface as null so the caller can fall back to legacy view
    if ((error as { response?: { status?: number }; status?: number })?.response?.status === 404 ||
        (error as { status?: number })?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Persist a coder override (code change, removal, manual add). The override
 * is captured as a training signal for F3 (hands-off learning).
 */
export async function submitCptOverride(
  appointmentId: string | undefined,
  itemId: string,
  args: SubmitOverrideArgs,
  encounterId?: string,
  isEncounterMode?: boolean,
): Promise<{ overrideId: string }> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  const response = await apiService.post<{ success: boolean; data: { overrideId: string } }>(
    `${baseUrl}/procedure-cpt/item/${itemId}/override`,
    args,
  );
  return response.data;
}

/**
 * Documentation Assistant — ask the LLM to suggest a CPT-friendlier rewrite
 * of a treatment note. Physician reviews the diff and accepts, edits, or
 * dismisses. Assistant never invents clinical facts.
 */
export interface DocumentationChangeDTO {
  before: string;
  after: string;
  reason: string;
}
export interface DocumentationSuggestionDTO {
  original: string;
  suggested: string;
  changes: DocumentationChangeDTO[];
  confidence: number;
  unchanged: boolean;
}

export async function suggestDocumentationRewrite(
  text: string,
  appointmentId?: string,
  encounterId?: string,
  isEncounterMode?: boolean,
): Promise<DocumentationSuggestionDTO> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  const response = await apiService.post<{ success: boolean; data: DocumentationSuggestionDTO }>(
    `${baseUrl}/procedure-cpt/documentation-assistant/rephrase`,
    { text },
  );
  return response.data;
}

// E&M Code API (Layer 1: Visit Billing)
export async function generateEMCode(
  appointmentId?: string,
  encounterId?: string,
  isEncounterMode?: boolean
): Promise<{ success: boolean; data: EMGenerateResponseData }> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  return await apiService.post<{ success: boolean; data: EMGenerateResponseData }>(
    `${baseUrl}/em-code/generate`,
    {}
  );
}

export async function getEMCode(
  appointmentId?: string,
  encounterId?: string,
  isEncounterMode?: boolean
): Promise<{ success: boolean; data: { emCode: EMCode | null } }> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  return await apiService.get<{ success: boolean; data: { emCode: EMCode | null } }>(
    `${baseUrl}/em-code`
  );
}

export async function reviewEMCode(
  appointmentId?: string,
  encounterId?: string,
  isEncounterMode?: boolean,
  action?: 'approve' | 'reject',
  notes?: string
): Promise<{ success: boolean; message: string; data: { emCode: EMCode } }> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  return await apiService.post<{ success: boolean; message: string; data: { emCode: EMCode } }>(
    `${baseUrl}/em-code/review`,
    { action, notes }
  );
}

export async function selectEMVariant(
  appointmentId: string | undefined,
  encounterId: string | undefined,
  isEncounterMode: boolean,
  patientType: 'new' | 'established',
): Promise<{ success: boolean; data: { emCode: EMCode } }> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  return await apiService.post<{ success: boolean; data: { emCode: EMCode } }>(
    `${baseUrl}/em-code/select-variant`,
    { patientType }
  );
}

/**
 * Fetch all active E&M codes from the catalog (em_codes table) for the manual picker.
 * Single shared endpoint — not scoped to appointment/encounter.
 */
export async function fetchEMCodeCatalog(): Promise<{
  success: boolean;
  data: { codes: EmCatalogEntry[] };
}> {
  return await apiService.get<{ success: boolean; data: { codes: EmCatalogEntry[] } }>(
    '/v1/clinical/em-code/catalog'
  );
}

/**
 * Persist a manually picked E&M code (physician override).
 */
export async function manualEntryEMCode(
  appointmentId: string | undefined,
  encounterId: string | undefined,
  isEncounterMode: boolean,
  code: string,
): Promise<{ success: boolean; data: { emCode: EMCode } }> {
  const baseUrl = getBaseUrl(appointmentId, encounterId, isEncounterMode);
  return await apiService.post<{ success: boolean; data: { emCode: EMCode } }>(
    `${baseUrl}/em-code/manual-entry`,
    { code }
  );
}
